import * as React from "react"
import { Icon } from "@iconify/react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select"
import { cn } from "../../lib/utils"

function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No results found.",
  disabled,
  className,
}) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")
  const inputRef = React.useRef(null)

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => o.label.toLowerCase().includes(q))
  }, [options, search])

  const handleOpenChange = (next) => {
    setOpen(next)
    if (next) {
      setSearch("")
      // Radix focuses the selected item once positioned (its own effect); grabbing
      // focus a frame later — after that effect and the paint it waits on — wins the race.
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }

  return (
    <Select value={value} onValueChange={onValueChange} open={open} onOpenChange={handleOpenChange} disabled={disabled}>
      <SelectTrigger className={className}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="p-0" onCloseAutoFocus={(e) => e.preventDefault()}>
        <div className="sticky top-0 z-10 border-b border-white/10 bg-[#16121f] p-1.5">
          <div className="relative">
            <Icon
              icon="ph:magnifying-glass-bold"
              className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground/60"
            />
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                // Keep keystrokes in the input instead of letting Radix's typeahead
                // steal focus to whichever item starts with the typed letter.
                if (e.key !== "Escape" && e.key !== "Tab") e.stopPropagation()
              }}
              placeholder={searchPlaceholder}
              className={cn(
                "w-full rounded-md border border-white/10 bg-white/[0.03] py-1.5 pl-7 pr-2 text-sm text-foreground",
                "placeholder:text-muted-foreground/50 outline-none transition-colors focus:border-primary/50"
              )}
            />
          </div>
        </div>
        <div className="p-1">
          {filtered.length === 0 ? (
            <p className="py-6 text-center text-xs text-muted-foreground">{emptyText}</p>
          ) : (
            filtered.map((o) => (
              <SelectItem key={o.value} value={o.value} disabled={o.disabled}>
                {o.label}
              </SelectItem>
            ))
          )}
        </div>
      </SelectContent>
    </Select>
  )
}

export { SearchableSelect }
